import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface CompleteAuditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (findings: string) => Promise<void>;
  isPending: boolean;
}

export function CompleteAuditDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: CompleteAuditDialogProps) {
  const [recordedInPmApp, setRecordedInPmApp] = useState(false);
  const [findings, setFindings] = useState("");

  useEffect(() => {
    if (open) {
      setRecordedInPmApp(false);
      setFindings("");
    }
  }, [open]);

  async function handleConfirm() {
    if (!recordedInPmApp) return;
    await onConfirm(findings.trim());
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Complete Audit</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="recorded-in-pm-app"
              checked={recordedInPmApp}
              onCheckedChange={(checked) => setRecordedInPmApp(checked === true)}
              className="mt-0.5"
            />
            <Label htmlFor="recorded-in-pm-app" className="cursor-pointer font-normal leading-snug">
              Indicate if the final audit has been recorded in the Project Management app.
            </Label>
          </div>

          <div className="space-y-1">
            <p className="text-sm font-medium">Audit Findings & Recommendations</p>
            <p className="text-xs text-muted-foreground">
              Document your final audit findings and provide actionable recommendations to improve
              compliance best practices.
            </p>
            <Textarea
              value={findings}
              onChange={(e) => setFindings(e.target.value)}
              rows={5}
              placeholder="Optional…"
              className="mt-2"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!recordedInPmApp || isPending} onClick={handleConfirm}>
            {isPending ? "Completing…" : "Complete Audit"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
